import { cn } from "@/lib/utils";

interface LoadingSpinnerProps {
  /** Additional CSS classes */
  className?: string;
  /** Size of the spinner */
  size?: "sm" | "md" | "lg" | "xl";
  /** Whether to center in a container with min-height */
  centered?: boolean;
  /** Custom label for accessibility */
  label?: string;
}

const sizeClasses = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-2",
  lg: "h-12 w-12 border-3",
  xl: "h-16 w-16 border-4",
} as const;

/**
 * Loading spinner component with accessibility support
 * Supports multiple sizes and centering options
 */
export function LoadingSpinner({
  className,
  size = "md",
  centered = true,
  label = "Loading..."
}: LoadingSpinnerProps) {
  const spinner = (
    <div
      role="status"
      aria-label={label}
      className={cn(
        "animate-spin rounded-full border-muted-foreground/30 border-t-primary",
        sizeClasses[size],
        className
      )}
    >
      <span className="sr-only">{label}</span>
    </div>
  );

  if (!centered) {
    return spinner;
  }

  return (
    <div className="flex items-center justify-center min-h-[200px]">
      {spinner}
    </div>
  );
}

/**
 * Inline loading spinner for buttons and small spaces
 */
export function InlineSpinner({ className }: { className?: string }) {
  return (
    <LoadingSpinner
      size="sm"
      centered={false}
      className={className}
      label="Loading"
    />
  );
}
